import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useAlerts, useFreezes, useSosReports, useResolveAlert, useResolveFreeze} from "@/hooks/useCompliance";
import type {PatternAlert, AccountFreeze, SosReport} from "@/services/compliance.service";
import {AlertTriangle, Lock, FileText} from "lucide-react";
import {useState} from "react";

type Tab = "alerts" | "freezes" | "sos";

export function Compliance() {
  const [tab, setTab] = useState<Tab>("alerts");

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-(--color-text-primary)">Compliance</h1>
        <p className="text-sm text-(--color-text-secondary) mt-1">Alertas, congelamientos y reportes SOS</p>
      </div>

      <div className="flex gap-2">
        <BaseButton
          variant={tab === "alerts" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setTab("alerts")}
        >
          <AlertTriangle size={14} /> Alertas
        </BaseButton>
        <BaseButton
          variant={tab === "freezes" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setTab("freezes")}
        >
          <Lock size={14} /> Congelamientos
        </BaseButton>
        <BaseButton
          variant={tab === "sos" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setTab("sos")}
        >
          <FileText size={14} /> Reportes SOS
        </BaseButton>
      </div>

      {tab === "alerts" && <AlertsTab />}
      {tab === "freezes" && <FreezesTab />}
      {tab === "sos" && <SosTab />}
    </div>
  );
}

function AlertsTab() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const {data, isLoading} = useAlerts({page, limit});
  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const resolveMutation = useResolveAlert();

  const riskColors: Record<string, "danger" | "warning" | "info" | "neutral"> = {
    very_high: "danger",
    high: "danger",
    medium: "warning",
    low: "info",
  };

  const columns: Column<PatternAlert>[] = [
    {key: "userId", header: "Usuario", render: row => row.userId.slice(0, 8)},
    {key: "patternType", header: "Patrón"},
    {
      key: "riskLevel",
      header: "Riesgo",
      render: row => (
        <BaseBadge
          variant={riskColors[row.riskLevel] ?? "neutral"}
          label={row.riskLevel}
        />
      ),
    },
    {
      key: "status",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.status === "resolved" || row.status === "false_positive" ? "success" : "warning"}
          label={row.status}
        />
      ),
    },
    {key: "createdAt", header: "Fecha", render: row => new Date(row.createdAt).toLocaleDateString()},
    {
      key: "actions",
      header: "",
      render: row =>
        row.status === "pending" || row.status === "reviewing" ? (
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => resolveMutation.mutate({id: row.id, body: {status: "resolved"}})}
          >
            Resolver
          </BaseButton>
        ) : null,
    },
  ];

  return (
    <BaseCard padding="none">
      <BaseTable<PatternAlert>
        data={items}
        columns={columns}
        loading={isLoading}
        emptyMessage="No hay alertas"
      />
      {pagination.totalPages > 1 && (
        <BasePagination
          page={page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={limit}
          onPageChange={setPage}
        />
      )}
    </BaseCard>
  );
}

function FreezesTab() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const {data, isLoading} = useFreezes({page, limit});
  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const resolveMutation = useResolveFreeze();

  const columns: Column<AccountFreeze>[] = [
    {key: "userId", header: "Usuario", render: row => row.userId.slice(0, 8)},
    {key: "freezeType", header: "Tipo"},
    {key: "reason", header: "Motivo", render: row => row.reason.slice(0, 40)},
    {key: "trigger", header: "Trigger"},
    {
      key: "isActive",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.isActive ? "danger" : "success"}
          label={row.isActive ? "Activo" : "Resuelto"}
        />
      ),
    },
    {key: "startedAt", header: "Inicio", render: row => new Date(row.startedAt).toLocaleDateString()},
    {
      key: "actions",
      header: "",
      render: row =>
        row.isActive ? (
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => resolveMutation.mutate({id: row.id, body: {resolution: "unlocked"}})}
          >
            Descongelar
          </BaseButton>
        ) : null,
    },
  ];

  return (
    <BaseCard padding="none">
      <BaseTable<AccountFreeze>
        data={items}
        columns={columns}
        loading={isLoading}
        emptyMessage="No hay congelamientos"
      />
      {pagination.totalPages > 1 && (
        <BasePagination
          page={page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={limit}
          onPageChange={setPage}
        />
      )}
    </BaseCard>
  );
}

function SosTab() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const {data, isLoading} = useSosReports({page, limit});
  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const statusColors: Record<string, "info" | "warning" | "success" | "neutral"> = {
    draft: "neutral",
    reviewing: "warning",
    sent: "success",
    archived: "info",
  };

  const columns: Column<SosReport>[] = [
    {key: "userId", header: "Usuario", render: row => row.userId.slice(0, 8)},
    {key: "currency", header: "Moneda"},
    {
      key: "totalSuspiciousCents",
      header: "Monto",
      render: row => (Number(row.totalSuspiciousCents) / 100).toLocaleString(),
    },
    {
      key: "status",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={statusColors[row.status] ?? "neutral"}
          label={row.status}
        />
      ),
    },
    {key: "regulatorDestination", header: "Regulador", render: row => row.regulatorDestination ?? "-"},
    {key: "createdAt", header: "Fecha", render: row => new Date(row.createdAt).toLocaleDateString()},
  ];

  return (
    <BaseCard padding="none">
      <BaseTable<SosReport>
        data={items}
        columns={columns}
        loading={isLoading}
        emptyMessage="No hay reportes SOS"
      />
      {pagination.totalPages > 1 && (
        <BasePagination
          page={page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={limit}
          onPageChange={setPage}
        />
      )}
    </BaseCard>
  );
}
